import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { 
  Package, 
  Clock,
  RefreshCw,
  TrendingUp,
  DollarSign,
  Tag
} from 'lucide-react'
import { useApi } from '../contexts/ApiContext'
import { useNotificationHelpers } from '../contexts/NotificationContext'
import { formatDate, formatTimeLeft, getStockStatus, getStockStatusColor, getRarityColor, getRarityBadgeColor, formatRarity, getRaritySortValue, getRarityShineClass, formatNumber, calculateMerchantSchedule, formatMerchantTime, getMerchantStatusText, getMerchantStatusColor } from '../lib/utils'
import { cn } from '../lib/utils'
import { StockCategory, ItemInfo } from '../types/api'

// Dynamic categories based on available stock data
const getCategories = (stockData: any) => {
  const baseCategories: { value: StockCategory | 'travelingmerchant'; label: string; color: string; bgColor: string; borderColor: string }[] = [
    { value: 'seed', label: 'Seeds', color: 'text-success', bgColor: 'bg-success/10', borderColor: 'border-success/30' },
    { value: 'gear', label: 'Gear', color: 'text-info', bgColor: 'bg-info/10', borderColor: 'border-info/30' },
    { value: 'egg', label: 'Eggs', color: 'text-purple', bgColor: 'bg-purple/10', borderColor: 'border-purple/30' },
    { value: 'cosmetic', label: 'Cosmetics', color: 'text-pink', bgColor: 'bg-pink/10', borderColor: 'border-pink/30' },
    { value: 'eventshop', label: 'Event Shop', color: 'text-orange', bgColor: 'bg-orange/10', borderColor: 'border-orange/30' },
  ]

  // Add traveling merchant only if it has stock
  if (stockData?.travelingmerchant_stock?.stock && stockData.travelingmerchant_stock.stock.length > 0) {
    baseCategories.push({
      value: 'travelingmerchant',
      label: 'Traveling Merchant',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-400/10',
      borderColor: 'border-yellow-400/30'
    })
  }

  return baseCategories
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

export default function StockTracker() {
  const { isAuthenticated, fetchStock, fetchItemInfo } = useApi()
  const { showSuccess, showWarning, showInfo } = useNotificationHelpers()
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000))

  // Real-time countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000))
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Calculate merchant schedule
  const merchantSchedule = calculateMerchantSchedule(currentTime)

  const { data: stockData, isLoading, refetch } = useQuery({
    queryKey: ['stock'],
    queryFn: fetchStock,
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Auto-refresh when items expire
  useEffect(() => {
    if (!stockData || !isAuthenticated) return

    const allStockItems = [
      ...stockData.seed_stock.map(item => ({ ...item, category: 'seed' as const })),
      ...stockData.gear_stock.map(item => ({ ...item, category: 'gear' as const })),
      ...stockData.egg_stock.map(item => ({ ...item, category: 'egg' as const })),
      ...stockData.cosmetic_stock.map(item => ({ ...item, category: 'cosmetic' as const })),
      ...stockData.eventshop_stock.map(item => ({ ...item, category: 'eventshop' as const })),
      // Add traveling merchant stock if available
      ...(stockData.travelingmerchant_stock?.stock?.map(item => ({ 
        ...item, 
        category: 'travelingmerchant' as const 
      })) || []),
    ]

    // Find items that are about to expire (within 1 second)
    const expiringItems = allStockItems.filter(item => {
      const timeLeft = item.end_date_unix - currentTime
      return timeLeft > 0 && timeLeft <= 1
    })

    if (expiringItems.length > 0) {
      // Schedule refresh for when items actually expire (at 0 seconds)
      const refreshTimer = setTimeout(() => {
        refetch()
        showInfo(
          'Stock Updated',
          `${expiringItems.length} item(s) have expired and been removed from stock.`
        )
      }, (expiringItems[0].end_date_unix - currentTime) * 1000) // Refresh exactly when items hit 0 seconds

      return () => clearTimeout(refreshTimer)
    }
  }, [currentTime, stockData, isAuthenticated, refetch, showInfo])

  // Merchant arrival/departure notifications
  useEffect(() => {
    if (!isAuthenticated) return

    // Check if merchant just arrived (within last 30 seconds)
    if (merchantSchedule.isActive && merchantSchedule.currentArrival) {
      const timeSinceArrival = currentTime - merchantSchedule.currentArrival
      if (timeSinceArrival <= 30) {
        showSuccess(
          'Traveling Merchant Arrived!',
          'The merchant has arrived with new items. Check the stock tracker for exclusive deals!'
        )
      }
    }

    // Check if merchant is about to depart (within 5 minutes)
    if (merchantSchedule.isActive && merchantSchedule.currentArrival) {
      const departureTime = merchantSchedule.currentArrival + (3 * 3600) // 3 hours after arrival
      const timeUntilDeparture = departureTime - currentTime
      
      if (timeUntilDeparture <= 300 && timeUntilDeparture > 240) { // Between 5 and 4 minutes
        showWarning(
          'Merchant Departing Soon!',
          'The traveling merchant will depart in 5 minutes. Make your final purchases!'
        )
      }
    }

    // Check if merchant is arriving soon (within 10 minutes)
    if (!merchantSchedule.isActive && merchantSchedule.timeUntilNext <= 600 && merchantSchedule.timeUntilNext > 540) {
      showInfo(
        'Merchant Arriving Soon!',
        `The traveling merchant will arrive in ${Math.floor(merchantSchedule.timeUntilNext / 60)} minutes.`
      )
    }
  }, [currentTime, merchantSchedule, isAuthenticated, showSuccess, showWarning, showInfo])

  // Notification triggers for stock updates (temporarily disabled for debugging)
  // useEffect(() => {
  //   if (stockData) {
  //     const allStockItems = [
  //       ...stockData.seed_stock.map(item => ({ ...item, category: 'seed' as const })),
  //       ...stockData.gear_stock.map(item => ({ ...item, category: 'gear' as const })),
  //       ...stockData.egg_stock.map(item => ({ ...item, category: 'egg' as const })),
  //       ...stockData.cosmetic_stock.map(item => ({ ...item, category: 'cosmetic' as const })),
  //       ...stockData.eventshop_stock.map(item => ({ ...item, category: 'eventshop' as const })),
  //     ]

  //     const lowStockItems = allStockItems.filter(item => item.quantity <= 10 && item.quantity > 0)
  //     const outOfStockItems = allStockItems.filter(item => item.quantity === 0)
      
  //     if (lowStockItems.length > 0) {
  //       showWarning(
  //         'Low Stock Alert',
  //         `${lowStockItems.length} item(s) are running low on stock. Consider restocking soon!`
  //       )
  //     }
      
  //     if (outOfStockItems.length > 0) {
  //       showWarning(
  //         'Out of Stock Alert',
  //         `${outOfStockItems.length} item(s) are completely out of stock.`
  //       )
  //     }
  //   }
  // }, [stockData, showWarning])



  const { data: itemInfoData } = useQuery({
    queryKey: ['itemInfo'],
    queryFn: () => fetchItemInfo(),
    enabled: isAuthenticated,
    staleTime: 300000, // Cache for 5 minutes
  })

  // Create a map of item_id to rarity for quick lookup
  const rarityMap = useMemo(() => {
    if (!itemInfoData) return new Map<string, string>()
    const map = new Map<string, string>()
    itemInfoData.forEach((item: ItemInfo) => {
      if (item.item_id && item.rarity) {
        map.set(item.item_id, item.rarity)
      }
    })
    return map
  }, [itemInfoData])

  const allStock = useMemo(() => {
    if (!stockData) return []
    
    const stock = [
      ...stockData.seed_stock.map(item => ({ ...item, category: 'seed' as const })),
      ...stockData.gear_stock.map(item => ({ ...item, category: 'gear' as const })),
      ...stockData.egg_stock.map(item => ({ ...item, category: 'egg' as const })),
      ...stockData.cosmetic_stock.map(item => ({ ...item, category: 'cosmetic' as const })),
      ...stockData.eventshop_stock.map(item => ({ ...item, category: 'eventshop' as const })),
      // Add traveling merchant stock if available
      ...(stockData.travelingmerchant_stock?.stock?.map(item => ({ 
        ...item, 
        category: 'travelingmerchant' as const,
        display_name: `${item.display_name} (Merchant)`
      })) || []),
    ]

    // Filter out expired items
    const activeStock = stock.filter(item => currentTime < item.end_date_unix)

    // Merge with item info data if available
    if (itemInfoData) {
      return activeStock.map(item => {
        const itemInfo = itemInfoData.find((info: ItemInfo) => info.item_id === item.item_id)
        if (itemInfo) {
          return {
            ...item,
            // Use item info data if stock data is missing
            price: item.price || itemInfo.price,
            description: item.description || itemInfo.description,
            effects: item.effects || itemInfo.effects,
            requirements: item.requirements || itemInfo.requirements,
            rarity: item.rarity || itemInfo.rarity,
          }
        }
        return item
      })
    }

    return activeStock
  }, [stockData, itemInfoData, currentTime])

  // Group items by category, maintaining global rarity sort order
  const groupedStock = useMemo(() => {
    const grouped = new Map<StockCategory, typeof allStock>()
    
    // Sort all items globally by rarity
    const sortedAllStock = [...allStock].sort((a, b) => {
      const rarityA = rarityMap.get(a.item_id) || 'unknown'
      const rarityB = rarityMap.get(b.item_id) || 'unknown'
      return getRaritySortValue(rarityA) - getRaritySortValue(rarityB)
    })
    
    // Then group by category while maintaining the global sort order
    const categories = getCategories(stockData)
    categories.forEach(category => {
      const categoryItems = sortedAllStock.filter(item => item.category === category.value)
      if (categoryItems.length > 0) {
        grouped.set(category.value, categoryItems)
      }
    })
    
    return grouped
  }, [allStock, rarityMap])

  if (!isAuthenticated) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <div className="text-center max-w-md space-modern">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-r from-info to-info/80 flex items-center justify-center mb-4">
              <Package className="h-8 w-8 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">Stock Tracker</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Please configure your API key using environment variables to view stock data.
          </p>
          <div className="glass-card p-6 text-left mt-6">
            <p className="text-sm font-medium mb-3">Setup Instructions:</p>
            <ol className="text-xs text-muted-foreground space-y-2">
              <li>1. Create a <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">.env</code> file in the project root</li>
              <li>2. Add <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">VITE_API_KEY=your_api_key</code></li>
              <li>3. Restart the development server</li>
            </ol>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-modern"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-modern-sm">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-info to-info/80 shadow-lg">
              <Package className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
                          <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Stock Tracker
            </h1>
              <p className="text-muted-foreground leading-relaxed">
                Real-time tracking of all available items in Grow a Garden
              </p>
            </div>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            refetch()
            // showSuccess('Stock Updated', 'Stock data has been refreshed successfully!')
          }}
          disabled={isLoading}
          className="btn btn-outline btn-md"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
          Refresh
        </motion.button>
      </div>

      {/* Traveling Merchant Status - Always show with synchronized timing */}
      <motion.div variants={itemVariants} className="gaming-card accent-yellow">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-yellow-400/20 to-yellow-400/10 border border-yellow-400/30">
                <div className="w-6 h-6 text-yellow-400">🛒</div>
              </div>
              <h3 className="card-title text-yellow-400">Traveling Merchant</h3>
            </div>
            <div className="text-right">
              {stockData?.travelingmerchant_stock?.stock && stockData.travelingmerchant_stock.stock.length > 0 ? (
                <>
                  <p className="text-sm font-semibold text-yellow-400">
                    {stockData.travelingmerchant_stock.stock.length} Items Available
                  </p>
                  {stockData.travelingmerchant_stock.arrival_time && stockData.travelingmerchant_stock.departure_time && (
                    <p className="text-xs text-muted-foreground">
                      Until {formatDate(stockData.travelingmerchant_stock.departure_time)}
                    </p>
                  )}
                </>
              ) : (
                <p className={cn("text-sm font-semibold", getMerchantStatusColor(merchantSchedule))}>
                  {getMerchantStatusText(merchantSchedule)}
                </p>
              )}
            </div>
          </div>
          <div className="mt-2">
            {stockData?.travelingmerchant_stock?.stock && stockData.travelingmerchant_stock.stock.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                {stockData.travelingmerchant_stock.merchantName || 'Mysterious Merchant'}
                {stockData.travelingmerchant_stock.location && ` • ${stockData.travelingmerchant_stock.location}`}
              </p>
            ) : (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Next arrival: {merchantSchedule.nextArrival ? formatMerchantTime(merchantSchedule.nextArrival) : 'Unknown'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Schedule: Every 4-6 hours • Duration: 2-3 hours
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Enhanced Stock List */}
      <motion.div variants={itemVariants} className="gaming-card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-success/20 to-success/10 border border-success/30">
                <TrendingUp className="h-6 w-6 text-success" />
              </div>
              <h3 className="card-title">Stock Items ({allStock.length})</h3>
            </div>
            {isLoading && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <div className="loading-spinner h-4 w-4"></div>
                <span>Updating...</span>
              </div>
            )}
          </div>
        </div>
        <div className="card-content">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="loading-spinner h-8 w-8"></div>
            </div>
          ) : allStock.length > 0 ? (
            <div className="space-y-8">
              {getCategories(stockData).map((category) => {
                const categoryItems = groupedStock.get(category.value)
                if (!categoryItems || categoryItems.length === 0) return null
                
                return (
                  <div key={category.value} className="space-y-4">
                    {/* Category Header */}
                    <div className="flex items-center space-x-3 pb-2 border-b border-border/30">
                      <div className={cn("p-2 rounded-lg border", category.bgColor, category.borderColor)}>
                        <div className={cn("w-4 h-4", category.color)} />
                      </div>
                      <h4 className={cn("text-lg font-semibold", category.color)}>
                        {category.label} ({categoryItems.length})
                      </h4>
                    </div>
                    
                    {/* Category Items */}
                    <div className="grid gap-3">
                      {categoryItems.map((item, index) => {
                        const rarity = rarityMap.get(item.item_id) || 'unknown'
                        const stockStatus = getStockStatus(item.quantity)
                        
                        return (
                          <motion.div
                            key={item.item_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={cn(
                              "flex items-center justify-between p-4 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 hover:scale-[1.02] relative",
                              getRarityShineClass(rarity)
                            )}
                          >
                            <div className="flex items-center space-x-4">
                              {item.icon && (
                                <div className={cn(
                                  "p-2 rounded-xl bg-muted/50 border border-border/30 relative",
                                  getRarityShineClass(rarity)
                                )}>
                                  <img src={item.icon} alt={item.display_name} className="w-8 h-8" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <p className="font-semibold text-foreground">{item.display_name}</p>
                                  <span className={cn(
                                    "text-xs font-medium px-2 py-1 rounded-lg border border-border/30 relative",
                                    getRarityBadgeColor(rarity),
                                    getRarityShineClass(rarity)
                                  )}>
                                    {formatRarity(rarity)}
                                  </span>
                                </div>
                                
                                {/* Item Details */}
                                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                  {/* Price */}
                                  <div className="flex items-center space-x-1">
                                    <DollarSign className="h-3 w-3" />
                                    <span>{item.price ? formatNumber(item.price) : 'N/A'}</span>
                                  </div>
                                  
                                  {/* Category */}
                                  <div className="flex items-center space-x-1">
                                    <Tag className="h-3 w-3" />
                                    <span className="capitalize">{item.category || 'Unknown'}</span>
                                  </div>
                                  
                                  {/* Effects */}
                                  {item.effects && item.effects.length > 0 && (
                                    <div className="flex items-center space-x-1">
                                      <span className="text-xs">✨</span>
                                      <span className="text-xs">{item.effects.join(', ')}</span>
                                    </div>
                                  )}
                                  
                                  {/* Requirements */}
                                  {item.requirements && item.requirements.length > 0 && (
                                    <div className="flex items-center space-x-1">
                                      <span className="text-xs">📋</span>
                                      <span className="text-xs">{item.requirements.join(', ')}</span>
                                    </div>
                                  )}
                                  
                                  {/* Description */}
                                  {item.description && (
                                    <span className="text-xs opacity-75 max-w-xs truncate" title={item.description}>
                                      {item.description}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-6">
                              {/* Quantity */}
                              <div className="text-right">
                                <p className={cn(
                                  "text-lg font-bold",
                                  stockStatus === 'out-of-stock' ? "text-destructive" : 
                                  stockStatus === 'low-stock' ? "text-warning" : "text-success"
                                )}>
                                  {formatNumber(item.quantity)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {stockStatus === 'out-of-stock' ? 'Out of Stock' :
                                   stockStatus === 'low-stock' ? 'Low Stock' : 'In Stock'}
                                </p>
                              </div>
                              
                              {/* Time Left */}
                              <div className="text-right">
                                <p className="text-sm font-semibold text-foreground">
                                  {formatTimeLeft(item.end_date_unix)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Time remaining
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                  <Package className="h-6 w-6 opacity-50" />
                </div>
                <p>No items found matching your criteria</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
} 